// billingsAPI.js
import { ENV } from "../Utils"; // Ajusta la ruta según tu proyecto

export class BillingsAPI {
  baseApi = ENV.BASE_API;

  /**
   * Obtener TODAS las facturas de los usuarios con un determinado role (o "all").
   * GET /billings/role/:role
   * @param {string} accessToken
   * @param {string} role - "partner", "institution", etc. o "all"
   */
  async getBillingsByRole(accessToken, role = "all") {
    try {
      const url = `${this.baseApi}/billings/role/${role}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(
          result.message || "Error al obtener las facturas por role",
        );
      }
      return result; // { code, message, data: [...] }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener TODAS las facturas asociadas a un usuario específico.
   * GET /billings/user/:userId
   * @param {string} accessToken
   * @param {string} userId
   */
  async getBillingsByUser(accessToken, userId) {
    try {
      const url = `${this.baseApi}/billings/user/${userId}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(
          result.message || "Error al obtener las facturas del usuario",
        );
      }
      return result; // { code, message, data: [...] }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtener una factura (EducaBilling) por su _id
   * GET /billings/:billingId
   * @param {string} accessToken
   * @param {string} billingId
   */
  async getBillingById(accessToken, billingId) {
    try {
      const url = `${this.baseApi}/billings/${billingId}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) {
        throw new Error(result.message || "Error al obtener la factura");
      }
      return result; // { code, message, data: { ... } }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Crear una nueva factura para un usuario. (POST /billings/create/:userId)
   * Se usa multipart/form-data si se suben archivos (billingdata, billingdatapartner).
   * @param {string} accessToken
   * @param {string} userId
   * @param {object} data
   *   Ejemplo data: {
   *     senderRole,
   *     billingNumber,
   *     description,
   *     billingtotal,
   *     billingpartner,
   *     status,
   *     file1 (para billingdata),
   *     file2 (para billingdatapartner)
   *   }
   */
  async createBilling(accessToken, userId, data) {
    try {
      // 1) Construir FormData
      const formData = new FormData();

      // Campos de texto
      if (data.senderRole) formData.append("senderRole", data.senderRole);
      if (data.billingNumber)
        formData.append("billingNumber", data.billingNumber);
      if (data.description) formData.append("description", data.description);
      if (data.billingtotal) formData.append("billingtotal", data.billingtotal);
      if (data.billingpartner)
        formData.append("billingpartner", data.billingpartner);
      if (data.status) formData.append("status", data.status);

      // Campos de archivo
      // data.file1 => se corresponde con billingdata
      // data.file2 => se corresponde con billingdatapartner
      if (data.file1) {
        formData.append("billingdata", data.file1);
      }
      if (data.file2) {
        formData.append("billingdatapartner", data.file2);
      }

      const url = `${this.baseApi}/billings/create/${userId}`;
      const params = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      };

      const response = await fetch(url, params);
      const result = await response.json();

      // El backend responde con status 201 al crear
      if (response.status !== 201) {
        throw new Error(result.message || "Error al crear la factura");
      }
      return result; // { code, message, data: {...} }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Actualizar una factura existente (PATCH /billings/update/:billingId)
   * Si se envían nuevos archivos, se sustituyen en el servidor.
   * @param {string} accessToken
   * @param {string} billingId
   * @param {object} data
   *   Estructura similar a createBilling. p.e. {
   *     senderRole, billingNumber, description, ...
   *     file1 => billingdata,
   *     file2 => billingdatapartner
   *   }
   */
  async updateBilling(accessToken, billingId, data) {
    try {
      const formData = new FormData();
      if (data.senderRole) formData.append("senderRole", data.senderRole);
      if (data.billingNumber)
        formData.append("billingNumber", data.billingNumber);
      if (data.description) formData.append("description", data.description);
      if (data.billingtotal) formData.append("billingtotal", data.billingtotal);
      if (data.billingpartner)
        formData.append("billingpartner", data.billingpartner);
      if (data.status) formData.append("status", data.status);

      if (data.file1) {
        formData.append("billingdata", data.file1);
      }
      if (data.file2) {
        formData.append("billingdatapartner", data.file2);
      }

      const url = `${this.baseApi}/billings/update/${billingId}`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      };

      const response = await fetch(url, params);
      const result = await response.json();
      // El backend responde con status 200 al actualizar
      if (response.status !== 200) {
        throw new Error(result.message || "Error al actualizar la factura");
      }

      return result; // { code, message, data: {...} }
    } catch (error) {
      throw error;
    }
  }
  async updateBillingFile(accessToken, billingId, isClient, file) {
    try {
      const formData = new FormData();

      // Si isClient = true => subimos "billingdata", caso contrario => "billingdatapartner"
      if (isClient) {
        formData.append("billingdata", file);
      } else {
        formData.append("billingdatapartner", file);
      }

      const url = `${this.baseApi}/billings/update/${billingId}`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      };

      const response = await fetch(url, params);
      const result = await response.json();

      if (response.status !== 200) {
        throw new Error(result.message || "Error al subir la factura");
      }

      return result; // { code, message, data: {...} }
    } catch (error) {
      throw error;
    }
  }
  /**
   * Eliminar una factura (DELETE /billings/:billingId)
   * @param {string} accessToken
   * @param {string} billingId
   */
  async deleteBilling(accessToken, billingId) {
    try {
      const url = `${this.baseApi}/billings/${billingId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) {
        throw new Error(result.message || "Error al eliminar la factura");
      }
      return result; // { code, message }
    } catch (error) {
      throw error;
    }
  }
}
